/**
 * Formats a date string or Date object to DD-MM-YYYY format
 * @param date - Date string or Date object
 * @returns Formatted date string in DD-MM-YYYY format, or empty string if date is invalid/null/undefined
 */
export const formatDateDDMMYYYY = (date: string | Date | null | undefined): string => {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return dateObj.toLocaleDateString('en-GB').replace(/\//g, '-');
};

/**
 * Formats a date string or Date object to ISO8601 format
 * @param date - Date string or Date object
 * @returns Formatted date string in ISO8601 format, or empty string if date is invalid/null/undefined
 */
export const formatDateISO8601 = (date: string | Date | null | undefined): string => {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return dateObj.toISOString();
};


/**
 * Gets the UTC offset in hours for the current timezone
 * @returns UTC offset in hours (positive = UTC+, negative = UTC-), or 0 if unable to determine
 */
export const getUtcOffsetInHours = (): number => {
  const offsetMinutes = new Date().getTimezoneOffset();
  
  // Check if offset is valid (getTimezoneOffset returns minutes with inverted sign)
  if (isNaN(offsetMinutes)) {
    return 0;
  }

  // Convert minutes to hours and invert sign (positive = UTC+, negative = UTC-)
  return -offsetMinutes / 60;
};


/**
 * Formats a date string or Date object to locale string
 * @param date - Date string or Date object
 * @returns Formatted date string in locale string, or empty string if date is invalid/null/undefined
 */
export const formatDateToLocaleString = (date?: Date | string | null) => {
  if (!date) {
    return "—";
  }

  // Check if date is valid
  const parsed = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return parsed.toLocaleString();
};
